import { formatBRL, formatL, PRODUTOS_LABEL } from "@/lib/api";
import {
  UserPlus, Users, ArrowRight, ChevronLeft, AlertTriangle, Wallet, Fuel,
  Gift, Trophy, Droplets, Gauge, CheckCircle2, XCircle, Printer, Car,
} from "lucide-react";

// Map produto → background color/gradient
const PRODUTO_GRADIENT = {
  gasolina_comum: "from-blue-600 to-blue-800",
  gasolina_aditivada: "from-orange-500 to-red-600",
  etanol: "from-emerald-500 to-green-700",
  diesel_s10: "from-slate-700 to-slate-900",
  diesel_s500: "from-yellow-500 to-amber-700",
};

const maskPlaca = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);

// Card de cashback (semáforo de validade) — compartilhado.
function CashbackCard({ cb }) {
  if (!cb || !(cb.saldo > 0)) return null;
  const dr = cb.dias_restantes;
  const cbColor = dr == null ? "neutral" : dr <= 3 ? "rose" : dr <= 7 ? "amber" : "emerald";
  const s = {
    neutral: { bg: "rgba(229,229,229,0.6)", border: "#D4D4D4", text: "#404040", chip: "bg-neutral-100 text-neutral-700" },
    rose:    { bg: "linear-gradient(135deg, #FECDD3 0%, #FDA4AF 100%)", border: "#E11D48", text: "#9F1239", chip: "bg-rose-100 text-rose-800" },
    amber:   { bg: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)", border: "#D97706", text: "#92400E", chip: "bg-amber-100 text-amber-800" },
    emerald: { bg: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)", border: "#059669", text: "#065F46", chip: "bg-emerald-100 text-emerald-800" },
  }[cbColor];
  return (
    <div data-testid="cashback-card" className="rounded-xl p-3.5 mb-3 border" style={{ background: s.bg, borderColor: s.border }}>
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold" style={{ color: s.text }}>
          <Wallet size={11} /> Saldo de cashback
        </div>
        {dr != null && (
          <span data-testid="cashback-days-chip" className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${s.chip}`}>
            {dr === 0 ? "expira hoje" : dr === 1 ? "expira amanhã" : `${dr} dias p/ expirar`}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5" style={{ color: s.text }}>
        <span className="font-serif-display text-3xl tracking-tight">R$ {cb.saldo.toFixed(2).replace(".", ",")}</span>
      </div>
      {cb.expira_em && (
        <div className="text-[10px] mt-1 leading-tight" style={{ color: s.text }}>
          Vence em {new Date(cb.expira_em).toLocaleDateString("pt-BR")}
          {dr != null && dr <= 3 && " · use antes que expire!"}
        </div>
      )}
      {cb.acumulando === false && (
        <div className="text-[10px] mt-1.5 leading-tight font-medium" style={{ color: s.text, opacity: 0.85 }}>
          <AlertTriangle size={11} className="inline-block -mt-0.5 mr-1" /> Acúmulo de cashback temporariamente desativado · saldo ainda pode ser usado
        </div>
      )}
    </div>
  );
}

// Entrada do atendimento: identificação pela PLACA (novo modelo anti-fraude).
export function PlacaStep({ placa, setPlaca, onSearch, onNew, onIndicacao }) {
  return (
    <div className="max-w-md mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-placa">
      <div className="chip mb-2 text-[10px] py-0.5">Atendimento · Início</div>
      <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1] mb-1">Placa do veículo</h1>
      <p className="text-[11px] text-neutral-500 mb-3">Digite a placa para identificar o cliente.</p>

      <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="card-soft rounded-xl p-3.5">
        <input
          data-testid="client-placa-input"
          value={placa} onChange={(e) => setPlaca(maskPlaca(e.target.value))}
          placeholder="ABC1D23"
          autoFocus maxLength={7} autoCapitalize="characters" autoComplete="off"
          className="digit-input uppercase"
          style={{ fontSize: 28, letterSpacing: "4px", textAlign: "center", padding: "12px 16px" }}
        />
        <div className="grid grid-cols-2 gap-1.5 mt-2.5">
          <button type="button" data-testid="new-client-btn" onClick={onNew}
                  className="btn-ghost h-10 text-[12px] font-medium flex items-center justify-center gap-1">
            <UserPlus size={12} /> Primeiro Acesso
          </button>
          <button type="submit" data-testid="search-client-btn"
                  className="btn-primary-blue h-10 text-[12px] font-medium flex items-center justify-center gap-1">
            <Car size={12} /> Identificar
          </button>
        </div>
      </form>

      <button data-testid="indicacao-btn" onClick={onIndicacao}
              className="mt-2 w-full card-soft rounded-xl p-2.5 flex items-center gap-2.5 text-left hover:!bg-amber-50/40 transition">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shrink-0">
          <Users className="text-white" size={13} />
        </div>
        <div className="flex-1">
          <div className="text-[12px] font-medium leading-tight">Campanha de Indicação</div>
          <div className="text-[10px] text-neutral-500 leading-tight mt-0.5">Gere 3 códigos para o cliente compartilhar</div>
        </div>
        <ArrowRight size={12} className="text-neutral-400" />
      </button>
    </div>
  );
}

// Verificação por PLACA: mostra nome + telefone mascarado; frentista digita os 4 últimos dígitos.
export function QuatroDigStep({ placaData, cashbackInfo, quatro, setQuatro, status, msg, onVerify, onBack, onAccessNoPhone, onLibEsp }) {
  const cor = status === "ok" ? "#059669" : status === "erro" ? "#E11D48" : null;
  const bloqueado = placaData?.bloqueado;
  const limite = placaData?.limite_atingido;
  const travado = bloqueado || limite;
  const placas = placaData?.placas || [];
  return (
    <div className="max-w-md mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-4dig">
      <button data-testid="back-btn" onClick={onBack} className="text-[11px] text-neutral-500 hover:text-neutral-900 mb-2 flex items-center gap-1">
        <ChevronLeft size={11} /> voltar
      </button>
      <div className="chip chip-green mb-2 text-[10px] py-0.5">Cliente identificado</div>
      <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1] mb-0.5">{placaData?.nome}</h1>
      <div className="text-[11px] text-neutral-500 mb-2">
        {placas.length ? placas.join(" · ") : "—"}
      </div>
      {placaData?.categoria === "app_driver" && (
        <div data-testid="badge-app-driver"
             className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-3"
             style={{ background: "linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%)", color: "#92400E" }}>
          <Car size={13} />
          <span className="text-[11px] font-semibold uppercase tracking-widest">Motorista de App</span>
        </div>
      )}

      <CashbackCard cb={cashbackInfo} />

      {travado ? (
        <div data-testid="acesso-travado" className="rounded-xl p-3.5 mb-2 border border-rose-200"
             style={{ background: "linear-gradient(135deg,#FECDD3 0%,#FDA4AF 100%)" }}>
          <div className="flex items-center gap-1.5 text-rose-900 font-semibold text-[13px]">
            <AlertTriangle size={15} />
            {bloqueado ? "Cadastro bloqueado por tentativas" : "Limite de acessos diário atingido"}
          </div>
          <p className="text-[11px] text-rose-800 mt-1">
            Libere com a senha de contingência (Liberação especial) para prosseguir.
          </p>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); onVerify(); }} className="card-soft rounded-xl p-3.5">
          <p className="text-[12px] text-neutral-600 mb-1">
            Confirme os <b>4 últimos dígitos</b> do telefone
            <b className="block text-[color:var(--ink)] text-xl font-bold tracking-wider mt-0.5">{placaData?.telefone_mascarado}</b>
          </p>
          <input
            data-testid="quatro-dig-input"
            value={quatro} onChange={(e) => setQuatro(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4} inputMode="numeric" autoFocus
            className="digit-input"
            style={{ fontSize: 32, letterSpacing: "8px", padding: "14px 16px", textAlign: "center",
                     ...(cor ? { borderColor: cor, boxShadow: `0 0 0 3px ${cor}26` } : {}) }}
          />
          {status === "erro" && (
            <div data-testid="quatro-dig-erro" className="text-[12.5px] text-rose-600 font-semibold mt-2 text-center flex items-center justify-center gap-1.5">
              <XCircle size={14} /> {msg || "Dígitos incorretos."}
            </div>
          )}
          <button type="submit" data-testid="verify-4dig-btn" disabled={quatro.length !== 4}
                  className="w-full mt-2.5 h-10 btn-primary-blue text-[12px] font-medium flex items-center justify-center gap-1.5">
            Confirmar acesso <ArrowRight size={12} />
          </button>
        </form>
      )}

      <div className="grid grid-cols-2 gap-1.5 mt-2">
        <button data-testid="access-no-phone-btn" onClick={onAccessNoPhone} className="btn-ghost h-10 text-[11px] font-medium">
          Sem celular
        </button>
        <button data-testid="lib-especial-btn" onClick={onLibEsp}
                className="h-10 rounded-full text-[11px] font-medium text-rose-700 hover:bg-rose-50 transition-colors flex items-center justify-center gap-1.5">
          <AlertTriangle size={11} /> Liberação especial
        </button>
      </div>
    </div>
  );
}

function ProgressCard({ icon: Icon, title, atingido, meta, colorClass, suffix, dataTestId }) {
  const pct = meta > 0 ? Math.min(100, (atingido / meta) * 100) : 0;
  return (
    <div className="card-soft rounded-xl p-2.5" data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-md bg-neutral-900 text-white flex items-center justify-center">
            <Icon size={10} />
          </div>
          <div className="text-[11px] font-medium leading-none">{title}</div>
        </div>
        <div className="text-[10px] text-neutral-500 font-medium">{Math.round(pct)}%</div>
      </div>
      <div className="progress-track mb-1" style={{ height: 6 }}>
        <div className={`progress-fill ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] font-medium leading-tight">
        {atingido.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} / {meta} <span className="text-[10px] text-neutral-500">{suffix}</span>
      </div>
    </div>
  );
}

// Prêmio Troca de Óleo — fica visível em TODO o atendimento (do login do cliente até o
// fim), enquanto o prêmio for válido e não utilizado. Antes ficava só na tela de
// combustível e sumia ao avançar, impedindo o cliente de usar o desconto.
export function OleoPremioBanner({ premio, onUsar }) {
  if (!premio?.disponivel) return null;
  const dias = premio.dias_restantes;
  const vence = dias == null
    ? "Disponível para usar"
    : dias <= 0 ? "Vence hoje" : `Vence em ${dias} dia${dias === 1 ? "" : "s"}`;
  return (
    <div data-testid="oleo-premio-card" className="rounded-xl p-3 border border-amber-300 shadow-lg"
         style={{ background: "linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 100%)" }}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
            <Droplets size={14} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[12px] font-semibold leading-tight text-amber-900">
              Prêmio Troca de Óleo — {Number(premio.percentual || 0).toLocaleString("pt-BR")}% de desconto
            </div>
            <div data-testid="oleo-premio-vence" className="text-[10px] text-amber-700">{vence}</div>
          </div>
        </div>
        <button data-testid="oleo-usar-btn" onClick={onUsar}
                className="btn-accent h-9 px-4 text-xs font-semibold shrink-0">
          Utilizar
        </button>
      </div>
    </div>
  );
}

export function ProductStep({ cliente, precos, descInfo, onSelect }) {
  const cfg = descInfo;
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-product">
      <div className="chip chip-green mb-2 text-[10px] py-0.5">{cliente?.nome}</div>
      <h1 className="font-serif-display text-xl md:text-2xl leading-[1.1] mb-0.5">Selecione o combustível</h1>
      <p className="text-[11px] text-neutral-500 mb-3">Descontos aplicados automaticamente.</p>

      {(cfg?.fidelidade_ativo || cfg?.oleo_ativo) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {cfg?.fidelidade_ativo && (
            <ProgressCard dataTestId="progress-fidelidade" icon={Trophy} title="Fidelidade"
                          atingido={cfg.fidelidade_atingido || 0} meta={cfg.fidelidade_meta || 0}
                          colorClass="" suffix="L" />
          )}
          {cfg?.oleo_ativo && (
            <ProgressCard dataTestId="progress-oleo" icon={Droplets} title="Troca de Óleo"
                          atingido={cfg.oleo_atingido || 0} meta={cfg.oleo_meta || 0}
                          colorClass="gold" suffix="L" />
          )}
        </div>
      )}

      {cfg?.descontos?.length > 0 && (
        <div className="card-soft rounded-xl p-2.5 mb-3">
          <div className="flex items-center gap-1.5 text-[11px] mb-1.5">
            <Gift size={11} className="text-[color:var(--brand-orange)]" />
            <span className="font-medium">{cfg.descontos.length} desconto{cfg.descontos.length > 1 ? "s" : ""} disponível{cfg.descontos.length > 1 ? "is" : ""}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {cfg.descontos.map((d) => (
              <span key={d.id} className="chip text-[10px] py-0.5 px-2">{d.label}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {precos.map((p, i) => (
          <button key={p.produto} data-testid={`select-produto-${p.produto}`} onClick={() => onSelect(p.produto)}
                  className="card-soft rounded-xl p-2.5 text-left group" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-center justify-between gap-2">
              <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${PRODUTO_GRADIENT[p.produto] || "from-slate-700 to-slate-900"} flex items-center justify-center shadow-sm shrink-0`}>
                <Fuel size={12} className="text-white" />
              </div>
              <ArrowRight size={12} className="text-neutral-300 group-hover:text-neutral-500 transition" />
            </div>
            <div className="font-serif-display text-base mt-1.5 mb-1.5 leading-tight">{PRODUTOS_LABEL[p.produto]}</div>
            <div className="grid grid-cols-2 gap-1.5 pt-1.5 border-t border-neutral-100">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-neutral-400">Bomba</div>
                <div className="text-[12px] font-medium text-neutral-700">{formatBRL(p.preco_bomba)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-[color:var(--brand-blue)]">Cadastro</div>
                <div className="text-[13px] font-semibold text-[color:var(--brand-blue)]">{formatBRL(p.preco_cadastro)}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ValueStep({ cliente: _cliente, produto, precos, descInfo, modo, setModo, valorInput, setValorInput, chosenDesc, setChosenDesc, onCalc, onBack, cumularAditivada }) {
  const produtoExibido = cumularAditivada ? "gasolina_aditivada" : produto;
  const preco = precos.find((p) => p.produto === produtoExibido);
  const grupoCumulativo = descInfo?.grupo_consumo?.regra === "cumulativo" && descInfo.grupo_consumo.desconto_centavos_litro > 0
    ? descInfo.grupo_consumo : null;

  // Compute effective price/L preview
  // REGRA v2.21: desconto incide sobre preco_cadastro (mesma base do backend).
  const previewPrecoEfetivo = (() => {
    if (!preco) return null;
    let p = preco.preco_cadastro;
    if (chosenDesc) {
      if (chosenDesc.tipo === "cashback" || chosenDesc.tipo === "indicacao") {
        // R$ off; preview only fixed price
      } else if (chosenDesc.desconto_centavos_litro) {
        p = preco.preco_cadastro - (chosenDesc.desconto_centavos_litro / 100);
      }
    }
    if (cumularAditivada) p -= 0.05;
    // v2.29: grupo de consumo com regra "cumulativo" — o desconto do grupo soma ao escolhido.
    if (grupoCumulativo) p -= (grupoCumulativo.desconto_centavos_litro || 0) / 100;
    return Math.max(0, p);
  })();

  return (
    <div className="max-w-md mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-value">
      <button onClick={onBack} data-testid="value-back-btn" className="text-[11px] text-neutral-500 hover:text-neutral-900 mb-2 flex items-center gap-1">
        <ChevronLeft size={11} /> voltar
      </button>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Produto</div>
      <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1] mb-1">{PRODUTOS_LABEL[produtoExibido]}</h1>
      {cumularAditivada && (
        <div className="chip chip-orange mb-1.5 text-[10px] py-0.5">Comum → Aditivada · +R$ 0,05/L</div>
      )}
      <div className="text-[11px] text-neutral-500 mb-3">
        Bomba {formatBRL(preco?.preco_bomba)} · Cadastro {formatBRL(preco?.preco_cadastro)}
      </div>
      {grupoCumulativo && (
        <div data-testid="grupo-cumulativo-info" className="mb-3 rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-[11.5px] text-[color:var(--brand-blue)]">
          <b>{grupoCumulativo.label}</b> — somado automaticamente ao desconto escolhido.
        </div>
      )}

      {descInfo?.descontos?.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Desconto {cumularAditivada && "(cumulativo)"}
          </div>
          <div className="space-y-1">
            <button data-testid="desc-option-none" onClick={() => setChosenDesc(null)}
                    className={`w-full p-2.5 rounded-lg text-left transition-all border ${!chosenDesc ? "bg-neutral-900 text-white border-neutral-900 shadow-sm" : "bg-white border-neutral-200 hover:border-neutral-300"}`}>
              <div className="font-medium text-[12px] leading-tight">Sem desconto especial</div>
              <div className="text-[10px] opacity-70 mt-0.5 leading-tight">Preço de cadastro padrão{cumularAditivada && " + bônus"}</div>
            </button>
            {descInfo.descontos.map((d) => (
              <button key={d.id} data-testid={`desc-option-${d.id}`} onClick={() => setChosenDesc(d)}
                      className={`w-full p-2.5 rounded-lg text-left transition-all border ${chosenDesc?.id === d.id ? "text-white border-transparent shadow-sm" : "bg-white border-neutral-200 hover:border-neutral-300"}`}
                      style={chosenDesc?.id === d.id ? { background: "linear-gradient(135deg,#1D4ED8 0%,#2563EB 100%)" } : {}}>
                <div className="font-medium text-[12px] leading-tight">{d.label}</div>
                <div className="text-[10px] opacity-80 mt-0.5 leading-tight">{d.info}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card-soft rounded-xl p-3.5">
        <div className="bg-neutral-100 rounded-full p-0.5 flex gap-0.5 w-fit mb-2.5">
          <button data-testid="modo-reais" onClick={() => setModo("reais")}
                  className={`px-3 h-7 rounded-full text-[11px] font-medium transition-colors ${modo === "reais" ? "bg-neutral-900 text-white" : "text-neutral-600"}`}>
            R$ Reais
          </button>
          <button data-testid="modo-litros" onClick={() => setModo("litros")}
                  className={`px-3 h-7 rounded-full text-[11px] font-medium transition-colors ${modo === "litros" ? "bg-neutral-900 text-white" : "text-neutral-600"}`}>
            Litros
          </button>
        </div>
        <input
          data-testid="valor-input"
          value={valorInput} onChange={(e) => setValorInput(e.target.value.replace(",", "."))}
          placeholder={modo === "reais" ? "100.00" : "20"}
          inputMode="decimal" autoFocus
          className="digit-input"
          style={{ fontSize: 26, padding: "12px 16px" }}
        />
        {previewPrecoEfetivo != null && Number(valorInput) > 0 && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-blue-700">Preço efetivo / litro</div>
              <div className="text-[14px] font-semibold text-blue-900">{formatBRL(previewPrecoEfetivo)}</div>
            </div>
            <Gauge size={14} className="text-blue-700/70" />
          </div>
        )}
        <button data-testid="value-next-btn" onClick={onCalc}
                className="w-full mt-2.5 h-10 btn-primary-blue text-[12px] font-medium flex items-center justify-center gap-1.5">
          Calcular <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

function PriceMini({ label, value, highlight }) {
  return (
    <div className={`rounded-lg px-2.5 py-1.5 ${highlight ? "bg-white/60" : "bg-white/35"}`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-700/80">{label}</div>
      <div className={`mt-0.5 ${highlight ? "text-[13px] font-semibold" : "text-[12px] font-medium"}`}>{value}</div>
    </div>
  );
}

export function ConfirmStep({ calc, onConfirm, onCancel }) {
  const semDesconto = Math.abs((calc?.desconto_total_reais || 0)) < 0.005;
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-confirm">
      <div className="chip chip-green mb-3 text-[10px] py-0.5"><Gauge size={10} /> Pronto para abastecer</div>

      {semDesconto ? (
        <div className="rounded-2xl p-5 md:p-6 text-neutral-900 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 60%,#F59E0B 100%)" }}>
          <div className="text-[10px] uppercase tracking-widest text-neutral-700 mb-1">Digite na bomba · cobre do cliente</div>
          <div data-testid="valor-unico" className="font-serif-display text-[44px] md:text-[68px] leading-[0.85] break-all">
            {formatBRL(calc.valor_liquido)}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <PriceMini label="Litros" value={formatL(calc.litros)} />
            <PriceMini label="Bomba/L" value={formatBRL(calc.preco_bomba)} />
            <PriceMini label="Efetivo/L" value={formatBRL(calc.preco_efetivo_por_litro)} highlight />
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-2.5">
          <div className="rounded-2xl p-4 md:p-5 text-neutral-900 relative overflow-hidden"
               style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 60%,#F59E0B 100%)" }}>
            <div className="text-[10px] uppercase tracking-widest text-neutral-700 mb-1">Digite na bomba</div>
            <div data-testid="valor-bomba" className="font-serif-display text-[36px] md:text-[52px] leading-[0.85] break-all">
              {formatBRL(calc.valor_bruto)}
            </div>
            <div className="mt-2 text-[11px] text-neutral-700">{formatL(calc.litros)} × {formatBRL(calc.preco_bomba)}/L</div>
          </div>
          <div className="rounded-2xl p-4 md:p-5 text-white relative overflow-hidden"
               style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#1D4ED8 60%,#2563EB 100%)" }}>
            <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1">Cobre do cliente</div>
            <div data-testid="valor-liquido" className="font-serif-display text-[36px] md:text-[52px] leading-[0.85] break-all">
              {formatBRL(calc.valor_liquido)}
            </div>
            <div className="mt-2 text-[11px] text-white/80">
              Desconto {formatBRL(calc.desconto_total_reais)} {calc.tipo_desconto ? `· ${calc.tipo_desconto}` : ""}
              {calc.grupo_consumo?.regra === "cumulativo" && calc.grupo_consumo.desconto_grupo_centavos > 0
                ? ` · + grupo ${calc.grupo_consumo.nome} (${Number(calc.grupo_consumo.desconto_grupo_centavos).toLocaleString("pt-BR")}¢/L)` : ""}
            </div>
            <div className="mt-2 pt-2 border-t border-white/15">
              <div className="text-white/60 uppercase tracking-widest text-[9px]">Preço efetivo / L</div>
              <div className="text-[14px] font-semibold mt-0.5">{formatBRL(calc.preco_efetivo_por_litro)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5 mt-3.5">
        <button data-testid="confirm-cancel-btn" onClick={onCancel}
                className="btn-ghost h-10 text-[12px] font-medium flex items-center justify-center gap-1">
          <XCircle size={13} /> Cancelar
        </button>
        <button data-testid="confirm-ok-btn" onClick={onConfirm}
                className="btn-primary-blue h-10 text-[12px] font-medium flex items-center justify-center gap-1">
          <CheckCircle2 size={13} /> Confirmar
        </button>
      </div>
    </div>
  );
}

export function AverageStep({ kmAtual, setKmAtual, litrosMedia, setLitrosMedia, calc, ultimoKm, onYes, onNo, mediaResult, finalInfo }) {
  return (
    <div className="max-w-md mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-average">
      <div className="chip mb-2 text-[10px] py-0.5">Pós-Venda</div>
      <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1] mb-3">Calcular média?</h1>

      {finalInfo?.atingiu_fidelidade && (
        <div className="card-soft rounded-lg p-2.5 mb-1.5 flex items-center gap-2"
             style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)" }}>
          <Trophy size={14} className="text-amber-700 shrink-0" />
          <div className="text-[11px] font-medium text-amber-900">Atingiu o Prêmio de Fidelidade!</div>
        </div>
      )}
      {finalInfo?.atingiu_oleo && (
        <div className="card-soft rounded-lg p-2.5 mb-1.5 flex items-center gap-2"
             style={{ background: "linear-gradient(135deg,#FED7AA 0%,#FDBA74 100%)" }}>
          <Droplets size={14} className="text-orange-700 shrink-0" />
          <div className="text-[11px] font-medium text-orange-900">
            Ganhou desconto na Troca de Óleo!
            {finalInfo?.cupom_oleo_codigo && <> Cupom Nº <b className="font-mono">{finalInfo.cupom_oleo_codigo}</b></>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5 mb-3">
        <button data-testid="media-no-btn" onClick={onNo} className="btn-ghost h-12 text-[12px] font-medium">
          Não, finalizar
        </button>
        <button data-testid="media-yes-btn" onClick={() => document.getElementById("media-form")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary-blue h-12 text-[12px] font-medium">
          Sim, calcular
        </button>
      </div>

      <div id="media-form" className="card-soft rounded-xl p-3.5 space-y-2.5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">
            KM Atual {ultimoKm ? `(último: ${ultimoKm})` : ""}
          </label>
          <input data-testid="km-input" value={kmAtual} onChange={(e) => setKmAtual(e.target.value)} type="number"
                 className="mt-1 w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">
            Litros (sugerido: {calc?.litros || "—"})
          </label>
          <input data-testid="litros-input" value={litrosMedia} onChange={(e) => setLitrosMedia(e.target.value)} type="number"
                 placeholder={String(calc?.litros || "")}
                 className="mt-1 w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
        </div>
        <button data-testid="media-calc-btn" onClick={onYes}
                className="w-full h-10 btn-accent text-[12px] font-medium flex items-center justify-center gap-1.5">
          Calcular e continuar <ArrowRight size={12} />
        </button>
        {mediaResult != null && (
          <div className="rounded-lg p-2.5 text-center bg-white border border-neutral-200">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Média</div>
            <div className="font-serif-display text-2xl mt-0.5">{mediaResult} <span className="text-xs text-neutral-500">km/L</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

export function CouponStep({ calc, cliente, finalInfo, mediaResult, onFinish }) {
  const print = () => window.print();
  return (
    <div className="max-w-md mx-auto px-4 md:px-6 pt-3 pb-8 float-in" data-testid="step-coupon">
      <div className="chip chip-green mb-2 text-[10px] py-0.5"><CheckCircle2 size={10} /> Atendimento concluído</div>
      <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1] mb-3">Comprovante</h1>

      <div id="print-area" className="thermal card-soft rounded-xl p-4 bg-white">
        <div className="text-center pb-2 mb-2 border-b border-dashed border-neutral-300">
          <div className="font-serif-display text-base">Comprovante de Atendimento</div>
          <div className="text-[10px] text-neutral-500 mt-0.5">{new Date().toLocaleString("pt-BR")}</div>
        </div>
        <div className="space-y-0.5 text-[11px]">
          <div>Cliente: <b>{cliente?.nome}</b></div>
          <div>Placa: {cliente?.placa}</div>
          <div>Telefone: {cliente?.telefone}</div>
        </div>
        <div className="border-t border-dashed border-neutral-300 my-2" />
        <div className="space-y-0.5 text-[11px]">
          <div>Litros: <b>{formatL(calc?.litros)}</b></div>
          <div>Valor Bruto: <b>{formatBRL(calc?.valor_bruto)}</b></div>
          <div>Desconto: <b>{formatBRL(calc?.desconto_total_reais)}</b></div>
          <div>Preço efetivo/L: <b>{formatBRL(calc?.preco_efetivo_por_litro)}</b></div>
          <div className="text-sm pt-1">Total Pago: <b>{formatBRL(calc?.valor_liquido)}</b></div>
        </div>
        {mediaResult && (
          <div className="mt-2 text-center font-serif-display text-base">Média: {mediaResult} km/L</div>
        )}
        {finalInfo?.cupom_oleo_codigo && (
          <div className="mt-2 border-t border-dashed border-neutral-300 pt-2 text-center text-[11px]">
            Prêmio Troca de Óleo — Cupom Nº <b className="font-mono">{finalInfo.cupom_oleo_codigo}</b>
          </div>
        )}
        {finalInfo?.mensagem_cupom && (
          <div className="mt-2 border-t border-dashed border-neutral-300 pt-2 text-center italic text-[10px] text-neutral-600">
            {finalInfo.mensagem_cupom}
          </div>
        )}
        {finalInfo?.recompensa_msg && (
          <div className="mt-1.5 rounded-md p-2 text-center text-[10px] font-medium"
               style={{ background: "linear-gradient(135deg,#F97316 0%,#DC2626 100%)", color: "#fff" }}>
            {finalInfo.recompensa_msg}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1.5 mt-3 no-print">
        <button data-testid="coupon-print-btn" onClick={print}
                className="btn-ghost h-10 text-[12px] font-medium flex items-center justify-center gap-1">
          <Printer size={12} /> Imprimir
        </button>
        <button data-testid="coupon-finish-btn" onClick={onFinish}
                className="btn-primary-blue h-10 text-[12px] font-medium flex items-center justify-center gap-1">
          Finalizar <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}
